import { Component, EventEmitter, forwardRef, Input, Output, Provider } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChipModule } from 'primeng/chip';
import { MultiSelectChangeEvent, MultiSelectModule } from 'primeng/multiselect';
import { ScrollerModule } from 'primeng/scroller';
import { TooltipModule } from 'primeng/tooltip';
export const MULTISELECT_CONTROL_VALUE_ACCESSOR: Provider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiSelectComponent),
    multi: true,
};

@Component({
    selector: 'lib-platform-multiselect',
    standalone: true,
    imports: [FormsModule, MultiSelectModule, TooltipModule, ChipModule, ScrollerModule],
    providers: [MULTISELECT_CONTROL_VALUE_ACCESSOR],
    template: `
        <p-multiSelect
            [options]="options"
            [(ngModel)]="value"
            [placeholder]="placeholder"
            [showClear]="true"
            appendTo="body"
            [optionLabel]="optionLabel"
            [optionValue]="optionValue"
            [dataKey]="dataKey"
            [filterBy]="filterBy"
            (onChange)="onSelectionChange($event)"
            (onHide)="onMultiSelectClose.emit()"
            [disabled]="isDisabled"
            [optionDisabled]="optionDisabled"
            display="chip"
            styleClass="p-multiSelect height-40"
            [virtualScroll]="virtualScroll"
            [virtualScrollItemSize]="virtualScrollItemSize"
            [lazy]="lazy"
            [filter]="true"
            (onLazyLoad)="onLazyLoadEvent($event)"
            (onFilter)="onFilterEvent($event)"
        >
            <ng-template let-item pTemplate="item">
                <div class="grid gap-2 flex-nowrap m-0 w-20rem">
                    <div class="col p-0 grid gap m-0 w-full" tooltipPosition="bottom" [pTooltip]="item[optionLabel]">
                        <span class="w-full overflow-hidden text-overflow-ellipsis">{{ item[optionLabel] }}</span>
                    </div>
                </div>
            </ng-template>
            <ng-template pTemplate="selectedItems" let-rowIndex>
                @for (item of value || []; track item; let idx = $index) {
                    @if (idx < 2) {
                        <p-chip
                            styleClass="text-sm py-0 radius-3xl mr-2"
                            class="filter-chips group-chips"
                            [removable]="true"
                            (onRemove)="removeItems(item, $event)"
                        >
                            <span
                                class="filter-chips group-chips w-2rem overflow-hidden text-overflow-ellipsis line-height-4"
                                tooltipPosition="bottom"
                                [pTooltip]="item[optionLabel]"
                                >{{ item[optionLabel] }}</span
                            >
                            <ng-template pTemplate="removeicon">
                                <div class="flex">
                                    <svg class="w-1rem h-1rem" viewBox="0 0 20 20">
                                        <rect width="20" height="20" rx="10" fill="#EAF0FB" />
                                        <path
                                            d="M6 14L14 6.00085"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M14 13.9991L6 6"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                            </ng-template>
                        </p-chip>
                    }
                }

                @if ((value || []).length > 2) {
                    <span class="p-multiselect-token" tooltipPosition="bottom" [pTooltip]="tooltipData.join(',')"
                        >+{{ (value.length || 0) - 2 }}</span
                    >
                }

                @if ((value || []).length === 0) {
                    <div>Select</div>
                }
            </ng-template>
        </p-multiSelect>
    `,
    styles: [
        `
            :host {
                display: block;
            }
        `,
    ],
})
export class MultiSelectComponent implements ControlValueAccessor {
    @Input() options: any[] = [];
    @Input() placeholder = 'Select options';
    @Input() optionLabel!: string;
    @Input() optionValue: string | undefined;
    @Input() dataKey: string | undefined;
    @Input() filterBy: string | undefined;
    @Input() optionDisabled: string | undefined;
    @Input() onWhenDisable: string | undefined;
    @Input() virtualScroll = false;
    @Input() virtualScrollItemSize = 40;
    @Input() lazy = false;

    @Output() onMultiSelectClose = new EventEmitter<void>();
    @Output() selection = new EventEmitter<any[]>();
    @Output() lazyLoad = new EventEmitter<{ first: number; last: number }>();
    @Output() filterChange = new EventEmitter<string>();

    value: any[] = [];
    isDisabled = false;
    tooltipData: string[] = [];

    // Callback functions to notify form about changes
    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    onLazyLoadEvent(event: { first: number; last: number }) {
        this.lazyLoad.emit(event);
    }

    onFilterEvent(event: { originalEvent: Event; filter: string }) {
        this.filterChange.emit(event.filter);
    }

    onSelectionChange(event: MultiSelectChangeEvent) {
        this.value = event.value || [];
        this.onChange(this.value);
        this.onTouched();
        this.selection.emit(this.value);
    }

    removeItems(item: any, clickEvent: MouseEvent): void {
        clickEvent.stopPropagation();
        this.value = this.value.filter((v) => v !== item);
        this.updateTooltipData(this.value);
        this.onChange(this.value);
        this.onTouched();
    }

    updateTooltipData(data: any[]) {
        this.tooltipData = data?.slice(2)?.map((item: any) => item[this.optionLabel]);
    }

    // ControlValueAccessor methods
    writeValue(value: any[]): void {
        this.value = value || [];
        this.updateTooltipData(this.value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }
}
